import {Color} from '@benchling/nucleus-ui';
import {Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {debounce} from 'ts-debounce';

import PrimaryFilterFormSkeleton from 'search/filters/base/PrimaryFilterFormSkeleton';
import {ButtonSize} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuWidth, OptionContentType} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';
import Option from 'ui-library/menus/Option';

export interface AuditLogColumnVisibilityDropdownProps {
  allColumns: Array<string>;
  hiddenColumns: Set<string>;
  onColumnToggle: (column: string) => void;
  onHideAll: () => void;
  onShowAll: () => void;
  disabled: boolean;
}

export default function AuditLogColumnVisibilityDropdown(
  props: AuditLogColumnVisibilityDropdownProps
): JSX.Element {
  const {allColumns, hiddenColumns, onColumnToggle, onHideAll, onShowAll, disabled} = props;

  const isAllVisible = hiddenColumns.size === 0;
  const someVisible = hiddenColumns.size < allColumns.length;
  const isIndeterminate = !isAllVisible && someVisible;

  // Clicking the label is triggering the column handlers twice, so we need to debounce them
  const debouncedShowAll = useMemo(() => debounce(onShowAll, 50), [onShowAll]);
  const debouncedHideAll = useMemo(() => debounce(onHideAll, 50), [onHideAll]);
  const debouncedColumnToggle = useMemo(() => debounce(onColumnToggle, 50), [onColumnToggle]);

  const getDropdownText = useCallback(() => {
    const hiddenCount = hiddenColumns.size;
    const totalColumns = allColumns.length;

    let dropdownText = '';
    if (hiddenCount === 0 || allColumns.length === 0) {
      dropdownText = `Displaying all ${totalColumns} columns`;
    } else if (hiddenCount === totalColumns) {
      dropdownText = 'All columns hidden';
    } else if (hiddenCount === 1) {
      dropdownText = '1 column hidden';
    } else {
      dropdownText = `${hiddenCount} columns hidden`;
    }

    return (
      <span>
        <BenchlingIcon icon="columns-three" className={css(styles.filterIcon)} />
        {dropdownText}
      </span>
    );
  }, [hiddenColumns, allColumns]);

  const menuContent = (
    <MenuPopover width={MenuWidth.LARGE} dropLeft={true}>
      <Option
        onSelect={() => {
          if (isAllVisible) {
            debouncedHideAll();
          } else {
            debouncedShowAll();
          }
        }}
        contentType={OptionContentType.PLAIN}
        className={css(styles.stickyShowAllOption)}
        dataTestAttrs={{component: 'AuditLogColumnVisibility', element: 'showAllOption'}}
      >
        <Checkbox
          id="audit-log-show-all-columns"
          checked={isAllVisible}
          indeterminate={isIndeterminate}
          label="Show all columns"
          onChange={() => {}}
        />
      </Option>
      <div className={css(styles.scrollableOptions)}>
        {allColumns.map((col) => {
          const isVisible = !hiddenColumns.has(col);
          return (
            <Option
              key={col}
              onSelect={() => {
                debouncedColumnToggle(col);
              }}
              contentType={OptionContentType.PLAIN}
              dataTestAttrs={{component: 'AuditLogColumnVisibility', element: 'columnOption'}}
            >
              <Checkbox id={`audit-log-column-${col}`} checked={isVisible} label={col} onChange={() => {}} />
            </Option>
          );
        })}
      </div>
    </MenuPopover>
  );

  return (
    <div className={css(styles.columnVisibilityDropdown)}>
      <PrimaryFilterFormSkeleton
        buttonSize={ButtonSize.SMALL}
        dropdownToggleText={getDropdownText()}
        menuOverride={menuContent}
        closeOnInsideClick={false}
        isActive={!isAllVisible}
        isDisabled={disabled}
        tooltipTitle={null}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  columnVisibilityDropdown: {
    minWidth: '150px',
  },
  stickyShowAllOption: {
    position: 'sticky',
    top: 0,
    backgroundColor: Color.WHITE,
    borderBottom: `1px solid ${Color.GRAY_200}`,
    zIndex: 1,
  },
  scrollableOptions: {
    overflowY: 'auto',
    maxHeight: '250px',
  },
  filterIcon: {
    marginRight: '4px',
  },
});
